import { useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useDataDomains, useDataObjects } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useWidgetPolling } from './useWidgetPolling';
import type { WidgetConfig, WidgetState } from './types';
import '@tylertech/forge/icon';
import './InformationStatisticsWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'information-statistics',
  title: 'Information',
  size: 'medium',
  refreshInterval: 30000,
  showRefresh: true,
  showExpand: false,
};

interface InformationStats {
  domainCount: number;
  objectCount: number;
}

export function InformationStatisticsWidget(): JSX.Element {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const {
    data: domainsData,
    loading: domainsLoading,
    error: domainsError,
    refetch: refetchDomains,
  } = useDataDomains(undefined, 2000);

  const {
    data: objectsData,
    loading: objectsLoading,
    error: objectsError,
    refetch: refetchObjects,
  } = useDataObjects(undefined, 2000);

  const loading = domainsLoading || objectsLoading;
  const error = domainsError || objectsError;

  const domainCount = domainsData?.dataDomains?.edges?.length ?? null;
  const objectCount = objectsData?.dataObjects?.edges?.length ?? null;

  const stats: InformationStats | null =
    domainCount !== null && objectCount !== null ? { domainCount, objectCount } : null;

  const widgetState: WidgetState<InformationStats> = {
    data: stats,
    loading,
    error: error?.message || null,
    lastRefresh,
  };

  const handleRefresh = useCallback(() => {
    refetchDomains();
    refetchObjects();
    setLastRefresh(new Date());
  }, [refetchDomains, refetchObjects]);

  useWidgetPolling({
    interval: WIDGET_CONFIG.refreshInterval!,
    onRefresh: handleRefresh,
    enabled: !loading && !error,
  });

  if ((domainsData || objectsData) && !lastRefresh) {
    setLastRefresh(new Date());
  }

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="information-statistics-widget__empty">
          <IconElement name="storage" className="information-statistics-widget__empty-icon" />
          <p className="forge-typography--body2">No information data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="information-statistics-widget__content">
        <div className="information-statistics-widget__counts">
          <div className="information-statistics-widget__count-card">
            <div className="information-statistics-widget__count-number">{stats.domainCount}</div>
            <div className="information-statistics-widget__count-label forge-typography--label2">
              Data Domains
            </div>
          </div>
          <div className="information-statistics-widget__count-divider" />
          <div className="information-statistics-widget__count-card">
            <div className="information-statistics-widget__count-number">{stats.objectCount}</div>
            <div className="information-statistics-widget__count-label forge-typography--label2">
              Data Objects
            </div>
          </div>
        </div>

        <div className="information-statistics-widget__footer">
          <Link
            to="/information"
            search={{ page: 1, pageSize: 25 }}
            className="information-statistics-widget__view-all forge-typography--body2"
          >
            View all →
          </Link>
        </div>
      </div>
    </DashboardWidget>
  );
}
